import { SystemStyleObject } from '@chakra-ui/react'

import { tokenVar } from '../../theme/tokenVar'
import { ColorScheme } from '../../types'

const tokens = {
  dark: {
    bgScrim: 'bg.scrim',
    textPrimary: 'text.primary',
    focusRing: 'interactive.focus-ring',
  },
  light: {
    bgScrim: 'light.bg.scrim',
    textPrimary: 'light.text.primary',
    focusRing: 'light.interactive.focus-ring',
  },
}

export type ModalSize = 'default' | 'compact' | 'medium' | 'large'

const modalDimensions: Record<ModalSize, { height: string; maxWidth: string }> =
  {
    default: { height: '774px', maxWidth: '1143.5px' },
    compact: { height: 'auto', maxWidth: '404.15px' },
    medium: { height: '400px', maxWidth: '640px' },
    large: { height: '500px', maxWidth: '880px' },
  }

const modalTitleTextStyles: Record<ModalSize, 'display-extended' | 'title'> = {
  default: 'display-extended',
  compact: 'title',
  medium: 'title',
  large: 'title',
}

export const overlayStyles = (
  colorScheme: ColorScheme,
  zIndex = 1000
): SystemStyleObject => ({
  position: 'fixed',
  inset: 0,
  zIndex,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bg: tokens[colorScheme].bgScrim,
})

export const FOLD_W = '71px'
export const FOLD_H = '42px'

const POLYGON =
  `polygon(0 0, calc(100% - ${FOLD_W}) 0, 100% ${FOLD_H}, ` +
  `100% 100%, 0 100%)`

/**
 * Clip wrapper for the modal content. Lives inside an outer box that carries
 * the drop-shadow filter and positioning context.
 */
export const containerWrapperStyles: SystemStyleObject = {
  position: 'relative',
  display: 'inline-flex',
  clipPath: POLYGON,
  zIndex: 1,
}

/**
 * Sibling of containerWrapperStyles — NOT a child, so it is unaffected by the
 * content wrapper's clip-path. Extends 2.649 px beyond the content wrapper on
 * every side and uses the same polygon, producing a correctly traced stroke
 * (including the diagonal cut) when the content wrapper sits on top.
 */
export const borderLayerStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  position: 'absolute',
  top: '-2.649px',
  left: '-2.649px',
  right: '-2.649px',
  bottom: '-2.649px',
  bg:
    colorScheme === 'light'
      ? 'light.interactive.tertiary-border'
      : 'interactive.tertiary-border',
  clipPath: POLYGON,
  zIndex: 0,
  pointerEvents: 'none',
})

export const containerStyles = (
  colorScheme: ColorScheme,
  maxW?: string,
  size: ModalSize = 'default'
): SystemStyleObject => {
  const dimensions = modalDimensions[size]

  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    bg: colorScheme === 'light' ? 'var(--bg-app, #F0F1F5)' : 'bg.card-elevated',
    borderRadius: '0',
    h: dimensions.height,
    maxH: '90vh',
    w: '90vw',
    maxW: maxW ?? dimensions.maxWidth,
    overflow: 'hidden',
  }
}

export const headerStyles = (
  size: ModalSize = 'default'
): SystemStyleObject => {
  const hasPresetSpacing = size === 'medium' || size === 'large'

  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    px: '24px',
    pt: hasPresetSpacing ? '24px' : '32px',
    pb: hasPresetSpacing ? '16px' : '0',
    ...(hasPresetSpacing && { pr: '96px' }),
    flexShrink: 0,
  }
}

export const titleStyles = (
  colorScheme: ColorScheme,
  size: ModalSize = 'default'
): SystemStyleObject => {
  const hasPresetSpacing = size === 'medium' || size === 'large'

  return {
    textStyle: modalTitleTextStyles[size],
    color: tokens[colorScheme].textPrimary,
    pr: hasPresetSpacing ? 0 : '40px',
  }
}

// Scrollbar thumb per scheme — the UA default scrollbar ignores the
// colorScheme entirely, so scrollable bodies style their own.
const SCROLLBAR_THUMB: Record<ColorScheme, string> = {
  dark: tokenVar('colors.border.strong'),
  light: tokenVar('colors.light.border.strong'),
}

export const bodyStyles = (
  size: ModalSize = 'default',
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  flex: 1,
  overflowY: size === 'compact' ? 'hidden' : 'auto',
  px: '24px',
  pt: size === 'medium' || size === 'large' ? '8px' : '36px',
  pb: size === 'medium' || size === 'large' ? '16px' : '24px',
  fontFamily: "'Soehne', sans-serif",
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '24px',
  // Standard properties (Firefox, Chrome 121+); WebKit pseudos as fallback.
  scrollbarWidth: 'thin',
  scrollbarColor: `${SCROLLBAR_THUMB[colorScheme]} transparent`,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: SCROLLBAR_THUMB[colorScheme],
    borderRadius: '4px',
  },
})

export const headerContentStyles: SystemStyleObject = {
  fontFamily: "'Soehne', sans-serif",
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '24px',
}

export const noteStyles: SystemStyleObject = {
  fontFamily: "'Soehne', sans-serif",
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '24px',
}

export const footerStyles = (
  size: ModalSize = 'default'
): SystemStyleObject => {
  const hasPresetSpacing = size === 'medium' || size === 'large'

  return {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: hasPresetSpacing ? '12px' : '8px',
    px: '24px',
    pb: '24px',
    pt: hasPresetSpacing ? '16px' : '30px',
    flexShrink: 0,
    width: '100%',
  }
}

export const catEarStyles = (colorScheme: ColorScheme): SystemStyleObject => ({
  position: 'absolute',
  top: 0,
  right: 0,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  w: '14px',
  h: '14px',
  border: 'none',
  cursor: 'pointer',
  background: 'transparent',
  color: colorScheme === 'light' ? '#FFFFFF' : tokens[colorScheme].textPrimary,
  _hover: {
    opacity: 0.7,
  },
  _focusVisible: {
    outline: '2px solid',
    outlineColor: tokens[colorScheme].focusRing,
    outlineOffset: '2px',
  },
})
